import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { TrendingUp, Users, Briefcase, Clock } from 'lucide-react';
import { analyticsAPI } from '../services/analyticsApi';
import DateRangeFilter from '../components/analytics/DateRangeFilter';
import AnalyticsKPICards from '../components/analytics/AnalyticsKPICards';
import HiringFunnelChart from '../components/analytics/HiringFunnelChart';
import TimeMetricsChart from '../components/analytics/TimeMetricsChart';
import SourceEffectivenessChart from '../components/analytics/SourceEffectivenessChart';
import './Analytics.css';

const STORAGE_KEY = 'hirefocuz-analytics-daterange';

const getInitialDateRange = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        range: parsed.range || '30d',
        customRange: {
          startDate: parsed.customRange?.startDate || null,
          endDate: parsed.customRange?.endDate || null
        }
      };
    }
  } catch {
    // Invalid stored data, use defaults
  }
  return { range: '30d', customRange: { startDate: null, endDate: null } };
};

const Analytics = () => {
  const initial = getInitialDateRange();
  const [dateRange, setDateRange] = useState(initial.range);
  const [customRange, setCustomRange] = useState(initial.customRange);

  // Persist date range to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        range: dateRange,
        customRange: dateRange === 'custom' ? customRange : null
      }));
    } catch {
      // localStorage not available
    }
  }, [dateRange, customRange]);

  const filters = dateRange === 'custom'
    ? { range: 'custom', ...customRange }
    : { range: dateRange };

  // Fetch overview KPIs
  const {
    data: overviewData,
    isLoading: overviewLoading,
    error: overviewError
  } = useQuery(
    ['analytics-overview', dateRange, customRange],
    () => analyticsAPI.getOverview(filters),
    { staleTime: 5 * 60 * 1000 }
  );

  // Fetch funnel metrics
  const {
    data: funnelData,
    isLoading: funnelLoading,
    error: funnelError
  } = useQuery(
    ['analytics-funnel', dateRange, customRange],
    () => analyticsAPI.getFunnel(filters),
    { staleTime: 5 * 60 * 1000 }
  );

  // Fetch time metrics
  const {
    data: timeData,
    isLoading: timeLoading,
    error: timeError
  } = useQuery(
    ['analytics-time', dateRange, customRange],
    () => analyticsAPI.getTimeMetrics(filters),
    { staleTime: 5 * 60 * 1000 }
  );

  // Fetch source metrics
  const {
    data: sourceData,
    isLoading: sourceLoading,
    error: sourceError
  } = useQuery(
    ['analytics-sources', dateRange, customRange],
    () => analyticsAPI.getSources(filters),
    { staleTime: 5 * 60 * 1000 }
  );

  const handleDateRangeChange = (range, custom) => {
    setDateRange(range);
    if (custom) {
      setCustomRange(custom);
    }
  };

  const hasError = overviewError || funnelError || timeError || sourceError;

  return (
    <div className="analytics-page">
      <header className="analytics-header">
        <div className="analytics-title">
          <TrendingUp className="analytics-title-icon" />
          <h1>Analytics & Reports</h1>
        </div>
        <DateRangeFilter
          value={dateRange}
          onChange={handleDateRangeChange}
          customRange={customRange}
        />
      </header>

      {hasError && (
        <div className="analytics-error">
          <p>Failed to load analytics data. Please try again later.</p>
        </div>
      )}

      {/* KPI Summary Cards */}
      <AnalyticsKPICards
        data={overviewData?.data}
        isLoading={overviewLoading}
      />

      {/* Charts Grid */}
      <div className="analytics-charts-grid">
        {/* Hiring Funnel */}
        <div className="analytics-chart-card analytics-chart-funnel">
          <div className="chart-card-header">
            <Users className="chart-icon" />
            <h2>Hiring Funnel</h2>
          </div>
          <HiringFunnelChart data={funnelData?.data} isLoading={funnelLoading} />
        </div>

        {/* Time Metrics */}
        <div className="analytics-chart-card analytics-chart-time">
          <div className="chart-card-header">
            <Clock className="chart-icon" />
            <h2>Time Metrics</h2>
          </div>
          <TimeMetricsChart data={timeData?.data} isLoading={timeLoading} />
        </div>

        {/* Source Effectiveness */}
        <div className="analytics-chart-card analytics-chart-source">
          <div className="chart-card-header">
            <Briefcase className="chart-icon" />
            <h2>Source Effectiveness</h2>
          </div>
          <SourceEffectivenessChart data={sourceData?.data} isLoading={sourceLoading} />
        </div>
      </div>
    </div>
  );
};

export default Analytics;
